// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only.

import { useEffect, useState } from 'react'
import { QRCodeSVG } from 'qrcode.react'
import { useAuth } from '../auth/AuthContext'
import * as api from '../api/client'

/**
 * Shown at login when an account is REQUIRED to use MFA but has not enrolled yet.
 * Provisions a TOTP secret via /auth/mfa/enroll-begin (using the login mfa_token, no session),
 * shows the QR + manual key, then verifies a code via /auth/mfa/verify — which confirms the
 * enrollment AND mints the session in one step.
 */
export default function MfaEnrollPrompt({ mfaToken, email, onSuccess }: {
  mfaToken: string
  email?: string
  onSuccess: () => void
}) {
  const { verifyMfa } = useAuth()
  const [enroll, setEnroll] = useState<api.MfaEnrollResponse | null>(null)
  const [code, setCode] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loadErr, setLoadErr] = useState<string | null>(null)

  useEffect(() => {
    let live = true
    api.mfaEnrollBegin(mfaToken)
      .then(r => { if (live) setEnroll(r) })
      .catch((e: any) => {
        if (!live) return
        setLoadErr(e?.body?.error === 'mfa_challenge_invalid'
          ? 'This sign-in attempt timed out. Please sign in again.'
          : 'Could not start MFA setup. Please sign in again.')
      })
    return () => { live = false }
  }, [mfaToken])

  async function submit(e?: React.FormEvent) {
    e?.preventDefault()
    if (submitting || code.length < 6) return
    setSubmitting(true)
    setError(null)
    const res = await verifyMfa(mfaToken, code)
    setSubmitting(false)
    if (res.ok) { onSuccess(); return }
    setError(
      res.error === 'invalid_code' ? 'That code is incorrect or expired. Try the next one your app shows.'
      : res.error === 'mfa_challenge_invalid' ? 'This sign-in attempt timed out. Please sign in again.'
      : 'Could not verify that code. Please try again.'
    )
  }

  if (loadErr) return <div style={{ fontSize: 14, color: '#b91c1c' }}>{loadErr}</div>
  if (!enroll) return <div style={{ fontSize: 14, color: 'var(--color-text-soft)' }}>Preparing MFA setup…</div>

  return (
    <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }} noValidate>
      <div style={{ fontSize: 14, color: 'var(--color-text-soft)' }}>
        Multi-factor authentication is required for {email ? email : 'your account'}. Set it up to continue.
      </div>
      <ol style={{ margin: 0, paddingLeft: 18, fontSize: 13.5, color: 'var(--color-text-soft)', lineHeight: 1.6 }}>
        <li>Open your authenticator app and add a new account.</li>
        <li>Scan this QR code (or enter the key manually).</li>
        <li>Enter the 6-digit code it shows to finish.</li>
      </ol>
      <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap', alignItems: 'flex-start' }}>
        <div style={{ background: '#fff', padding: 12, borderRadius: 8, border: '1px solid var(--color-border)' }}>
          <QRCodeSVG value={enroll.otpauth_uri} size={160} />
        </div>
        <div style={{ flex: '1 1 200px', minWidth: 200 }}>
          <div style={{ fontSize: 12, color: 'var(--color-text-mute)', marginBottom: 4 }}>Can&rsquo;t scan? Enter this key:</div>
          <code style={{ display: 'block', wordBreak: 'break-all', fontSize: 13, background: 'var(--color-surface-2, #f3f4f6)', padding: '8px 10px', borderRadius: 6, marginBottom: 12 }}>{enroll.secret}</code>
          <input
            inputMode="numeric" autoComplete="one-time-code" autoFocus maxLength={6} placeholder="000000"
            value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            style={{ width: 140, padding: '10px 12px', borderRadius: 8, border: '1px solid var(--color-border)', fontSize: 18, letterSpacing: 4, textAlign: 'center' }}
          />
        </div>
      </div>
      {error && <div style={{ fontSize: 13, color: '#b91c1c' }}>{error}</div>}
      <button type="submit" disabled={submitting || code.length < 6}
        style={{ padding: '11px 14px', borderRadius: 8, border: 'none', background: 'var(--color-accent, #2563eb)', color: '#fff', fontWeight: 600, cursor: submitting || code.length < 6 ? 'default' : 'pointer', opacity: submitting || code.length < 6 ? 0.6 : 1 }}>
        {submitting ? 'Verifying…' : 'Verify & finish setup'}
      </button>
    </form>
  )
}
